import { useState, useEffect, useRef } from 'react'
import { Terminal, X, Trash2, Download, Search, Code2, ExternalLink, ChevronDown, ChevronRight, Bug, AlertCircle, Info, CheckCircle } from 'lucide-react'
import { useStore } from '../store/useStore'
import { Console as ConsoleFeed } from 'console-feed'
import * as StackTrace from 'stacktrace-js'

export default function EnhancedDebugConsole() {
  const { toggleConsole, setCurrentFile, consoleMessages, clearConsole: clearStoreConsole } = useStore()
  const files = useStore(state => state.files) // Read files separately for navigateToError (not in deps)
  const [processedLogs, setProcessedLogs] = useState([])
  const [filter, setFilter] = useState('')
  const [showErrors, setShowErrors] = useState(true)
  const [showWarnings, setShowWarnings] = useState(true)
  const [showInfo, setShowInfo] = useState(true)
  const [showLogs, setShowLogs] = useState(true)
  const [hoveredError, setHoveredError] = useState(null)
  const [expandedLogs, setExpandedLogs] = useState(new Set())
  const [errorDetails, setErrorDetails] = useState(new Map())
  const [groupedLogs, setGroupedLogs] = useState([])
  
  const scrollRef = useRef(null)

  // Create a unique hash for a log message to group identical errors
  const getLogHash = (log) => {
    const method = log.method || 'log'
    const dataString = log.data?.map(d => {
      if (typeof d === 'object' && d.message) return d.message
      if (typeof d === 'object') return JSON.stringify(d)
      return String(d)
    }).join(' ') || ''
    
    // Include stack trace in hash for errors
    const stack = log.data?.[0]?.stack || ''
    return `${method}:${dataString}:${stack.substring(0, 100)}`
  }

  // Group logs with identical messages
  useEffect(() => {
    const groups = new Map()
    
    processedLogs.forEach(log => {
      const hash = getLogHash(log)
      
      if (groups.has(hash)) {
        const existing = groups.get(hash)
        existing.count++
        existing.timestamps.push(log.timestamp)
        existing.ids.push(log.id)
      } else {
        groups.set(hash, {
          ...log,
          count: 1,
          timestamps: [log.timestamp],
          ids: [log.id],
          hash
        })
      }
    })
    
    setGroupedLogs(Array.from(groups.values()))
  }, [processedLogs])

  // Clear console
  const clearConsole = () => {
    clearStoreConsole()
    setProcessedLogs([])
    setErrorDetails(new Map())
    setExpandedLogs(new Set())
  }

  // Export logs
  const exportLogs = () => {
    const logText = processedLogs.map(log => {
      const timestamp = new Date(log.timestamp || Date.now()).toISOString()
      const level = log.method?.toUpperCase() || 'LOG'
      const message = log.data?.map(d => {
        if (typeof d === 'object') return JSON.stringify(d, null, 2)
        return String(d)
      }).join(' ') || ''
      
      const details = errorDetails.get(log.id)
      if (details) {
        return `[${timestamp}] [${level}] ${message}\nStack Trace:\n${details.stackString || 'N/A'}\n`
      }
      return `[${timestamp}] [${level}] ${message}`
    }).join('\n\n')
    
    const blob = new Blob([logText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `console-logs-${Date.now()}.txt`
    a.click()
    // Delay revoke to ensure download starts before URL is invalidated
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Toggle log expansion - with lazy stack trace parsing
  const toggleLogExpansion = async (logId, rawError) => {
    const newExpanded = new Set(expandedLogs)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
      
      // PERF: Lazy stack trace parsing - only parse when first expanding
      if (rawError && !errorDetails.has(logId)) {
        const enhanced = await enhanceStackTrace(rawError)
        // Always set details - even if parsing failed - to prevent infinite loading state
        setErrorDetails(prev => {
          const newMap = new Map(prev)
          newMap.set(logId, enhanced || {
            frames: [],
            rawStack: rawError?.stack || 'Stack trace unavailable',
            stackString: rawError?.stack || 'Stack trace unavailable',
            parseError: true
          })
          return newMap
        })
      }
    }
    setExpandedLogs(newExpanded)
  }

  // Parse and enhance error stack trace
  const enhanceStackTrace = async (error) => {
    try {
      // Try to parse the stack trace using stacktrace-js
      const stackFrames = await StackTrace.fromError(error)
      
      // Enhance each stack frame with source map info if available
      const enhancedFrames = await Promise.all(stackFrames.map(async (frame) => {
        // Clean up the filename
        let fileName = frame.fileName || ''
        
        // Check if it's an iframe ID (UUID pattern) or data URL
        if (fileName.match(/^[a-f0-9-]{36}$/)) {
          fileName = 'sketch.js'
        } else if (fileName.startsWith('data:')) {
          fileName = 'sketch.js'
        } else if (fileName.includes('/')) {
          // Keep full path for library files
          const parts = fileName.split('/')
          fileName = parts.length > 2 ? parts.slice(-2).join('/') : parts.pop()
        }
        
        return {
          functionName: frame.functionName || '<anonymous>',
          fileName: fileName,
          lineNumber: frame.lineNumber || 0,
          columnNumber: frame.columnNumber || 0,
          source: frame.toString()
        }
      }))
      
      return {
        frames: enhancedFrames,
        stackString: enhancedFrames.map(f => 
          `    at ${f.functionName} (${f.fileName}:${f.lineNumber}:${f.columnNumber})`
        ).join('\n'),
        rawStack: error.stack
      }
    } catch (err) {
      console.warn('Stack trace parsing failed:', err)
      // Fallback to parsing raw stack string
      if (error.stack) {
        const frames = parseRawStack(error.stack)
        return {
          stackString: error.stack,
          frames: frames,
          rawStack: error.stack
        }
      }
      return null
    }
  }
  
  // Parse raw stack string as fallback
  const parseRawStack = (stackString) => {
    const frames = []
    const lines = stackString.split('\n')
    
    for (const line of lines) {
      // Match patterns like "at functionName (file:line:col)" or "functionName@file:line:col"
      const match1 = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/)
      const match2 = line.match(/at\s+(.+?):(\d+):(\d+)/)
      const match3 = line.match(/(.+?)@(.+?):(\d+):(\d+)/)
      
      let match = match1 || match2 || match3
      if (match) {
        let functionName = match[1] || '<anonymous>'
        let fileName = match[2] || (match1 ? match[2] : match[1])
        let lineNumber = parseInt(match[match.length - 2])
        let columnNumber = parseInt(match[match.length - 1])
        
        // Clean up file name
        if (fileName.startsWith('data:')) {
          fileName = 'sketch.js'
        } else if (fileName.includes('/')) {
          const parts = fileName.split('/')
          fileName = parts.length > 2 ? parts.slice(-2).join('/') : parts.pop()
        }
        
        frames.push({
          functionName,
          fileName,
          lineNumber,
          columnNumber
        })
      }
    }
    
    return frames
  }

  // Navigate to error in editor
  const navigateToError = (frame) => {
    if (!frame || !frame.fileName) return
    
    // Find the file in our files list
    const targetFile = files.find(f => f.name === frame.fileName) || 
                      files.find(f => f.name === 'sketch.js')
    
    if (targetFile) {
      // Switch to the file
      setCurrentFile(targetFile.name)
      
      // Send event to highlight line in editor
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('highlightError', {
          detail: {
            lineNumber: frame.lineNumber,
            columnNumber: frame.columnNumber
          }
        }))
      }, 100)
    }
  }

  // Process console messages from store into console-feed format
  // PERF: Only depends on consoleMessages, not files (files only needed for navigation on click)
  // PERF: Uses stable IDs from store instead of generating random ones
  // PERF: Stack trace parsing is lazy (on expand), not upfront
  useEffect(() => {
    const processed = consoleMessages.map((rawMessage, index) => {
      const { id, level, message, error, stack, fileName, lineNumber, columnNumber, timestamp } = rawMessage
      
      // Use stable ID from store, fallback to index-based ID
      const stableId = id || `fallback-${index}`
      
      // Build a log entry that console-feed can understand
      const logEntry = {
        method: level || 'log',
        data: [],
        timestamp: timestamp ? new Date(timestamp).getTime() : Date.now(),
        id: stableId,
        // Store raw error info for lazy stack parsing on expand
        _rawError: null
      }

      // Handle different message types
      // Priority: error object > stack string > plain message
      if ((error && typeof error === 'object' && error.stack) || stack || (level === 'error' && message)) {
        // Create error object from serialized data
        const errorMessage = error?.message || message || 'Unknown error'
        const errorStack = error?.stack || stack
        
        const errorObj = new Error(errorMessage)
        if (errorStack) {
          errorObj.stack = errorStack
        }
        errorObj.name = error?.name || 'Error'
        
        // Preserve location info
        if (fileName || error?.fileName) {
          errorObj.fileName = fileName || error.fileName
          errorObj.lineNumber = lineNumber || error.lineNumber
          errorObj.columnNumber = columnNumber || error.columnNumber
        }
        
        logEntry.data = [errorObj]
        // Store for lazy parsing
        logEntry._rawError = errorObj
      } else {
        // Regular non-error message
        try {
          // Try to parse if it's JSON
          const parsed = JSON.parse(message)
          logEntry.data = [parsed]
        } catch {
          // Otherwise just use the message
          logEntry.data = [message]
        }
      }

      return logEntry
    })
    
    setProcessedLogs(processed)
    
    // Clean up errorDetails for IDs that no longer exist
    const validIds = new Set(processed.map(p => p.id))
    setErrorDetails(prev => {
      const newMap = new Map()
      for (const [key, value] of prev) {
        if (validIds.has(key)) {
          newMap.set(key, value)
        }
      }
      return newMap
    })
  }, [consoleMessages])

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [processedLogs])

  // Filter logs (use grouped logs for better error stacking)
  const filteredLogs = groupedLogs.filter(log => {
    // Filter by type
    if (!showErrors && log.method === 'error') return false
    if (!showWarnings && log.method === 'warn') return false
    if (!showInfo && log.method === 'info') return false
    if (!showLogs && log.method === 'log') return false
    
    // Filter by search
    if (filter) {
      const searchStr = filter.toLowerCase()
      const logStr = log.data?.map(d => {
        if (typeof d === 'object') return JSON.stringify(d)
        return String(d)
      }).join(' ').toLowerCase() || ''
      return logStr.includes(searchStr)
    }
    
    return true
  })

  // Count log types
  const errorCount = processedLogs.filter(l => l.method === 'error').length
  const warnCount = processedLogs.filter(l => l.method === 'warn').length
  const infoCount = processedLogs.filter(l => l.method === 'info').length
  const logCount = processedLogs.filter(l => l.method === 'log').length

  // Get icon for log level
  const getLevelIcon = (level) => {
    switch(level) {
      case 'error': return <AlertCircle size={14} className="text-ide-error" />
      case 'warn': return <AlertCircle size={14} className="text-ide-warning" />
      case 'info': return <Info size={14} className="text-ide-accent" />
      default: return <CheckCircle size={14} className="text-ide-textDim" />
    }
  }

  return (
    <div className="bg-ide-panel h-full flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 bg-ide-surface border-b border-ide-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Terminal size={14} className="text-ide-textMuted" />
              <span className="text-sm font-medium text-ide-text">Console</span>
            </div>
            
            {/* Filter buttons */}
            <div className="flex items-center gap-1 text-xs">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className={`px-1.5 py-1 rounded transition-colors flex items-center gap-1 ${
                  showErrors ? 'bg-ide-error/20 text-ide-error' : 'bg-ide-surfaceHover text-ide-textDim'
                }`}
                title="Toggle errors"
              >
                <Bug size={12} />
                {errorCount}
              </button>
              <button
                onClick={() => setShowWarnings(!showWarnings)}
                className={`px-1.5 py-1 rounded transition-colors flex items-center gap-1 ${
                  showWarnings ? 'bg-ide-warning/20 text-ide-warning' : 'bg-ide-surfaceHover text-ide-textDim'
                }`}
                title="Toggle warnings"
              >
                <AlertCircle size={12} />
                {warnCount}
              </button>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className={`px-1.5 py-1 rounded transition-colors flex items-center gap-1 ${
                  showInfo ? 'bg-ide-accent/20 text-ide-accent' : 'bg-ide-surfaceHover text-ide-textDim'
                }`}
                title="Toggle info"
              >
                <Info size={12} />
                {infoCount}
              </button>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className={`px-1.5 py-1 rounded transition-colors flex items-center gap-1 ${
                  showLogs ? 'bg-ide-textDim/20 text-ide-textMuted' : 'bg-ide-surfaceHover text-ide-textDim'
                }`}
                title="Toggle logs"
              >
                <CheckCircle size={12} />
                {logCount}
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Search */}
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ide-textDim" />
              <input
                type="text"
                placeholder="Filter..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-7 pr-2 py-1 bg-ide-bg border border-ide-border rounded text-xs w-24 text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus"
              />
            </div>
            
            <button
              onClick={exportLogs}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Export logs"
              aria-label="Export logs"
            >
              <Download size={14} />
            </button>
            <button
              onClick={clearConsole}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Clear console"
              aria-label="Clear console"
            >
              <Trash2 size={14} />
            </button>
            <button
              onClick={toggleConsole}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Close console"
              aria-label="Close console"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Info bar */}
      <div className="px-3 py-1 bg-ide-bg text-xs text-ide-textDim border-b border-ide-border flex-shrink-0">
        <span>💡 Errors grouped • Click to expand • Click frames to jump</span>
      </div>

      {/* Console Content */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-2 bg-ide-bg"
      >
        {filteredLogs.map(log => {
          const isExpanded = expandedLogs.has(log.id)
          const details = errorDetails.get(log.id)
          const isError = log.method === 'error'
          const hasRawError = log._rawError != null
          const isExpandable = isError && hasRawError
          
          return (
            <div 
              key={log.id}
              className={`mb-1 rounded ${
                isError ? 'bg-ide-error/10 border border-ide-error/20' : 
                log.method === 'warn' ? 'bg-ide-warning/10 border border-ide-warning/20' :
                'border border-transparent'
              }`}
            >
              {/* Main log content */}
              <div 
                className={`p-2 flex items-start gap-2 ${
                  isExpandable ? 'cursor-pointer hover:bg-ide-error/20' : ''
                }`}
                onClick={() => isExpandable && toggleLogExpansion(log.id, log._rawError)}
              >
                <div className="flex items-center gap-1 mt-0.5">
                  {isExpandable && (
                    isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
                  )}
                  {getLevelIcon(log.method)}
                  
                  {/* Count badge for grouped errors */}
                  {log.count > 1 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      log.method === 'error' ? 'bg-ide-error text-white' :
                      log.method === 'warn' ? 'bg-ide-warning text-white' :
                      'bg-ide-textDim text-white'
                    }`}>
                      {log.count}
                    </span>
                  )}
                </div>
                
                <div className="flex-1 overflow-hidden">
                  <ConsoleFeed
                    logs={[log]}
                    variant="dark"
                    styles={{
                      BASE_FONT_FAMILY: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                      BASE_FONT_SIZE: '12px',
                      BASE_LINE_HEIGHT: '1.4',
                      BASE_BACKGROUND_COLOR: 'transparent',
                      LOG_COLOR: '#e0e0e0',
                      LOG_BACKGROUND: 'transparent',
                      LOG_BORDER: 'none',
                      LOG_ERROR_COLOR: '#ff6b6b',
                      LOG_WARN_COLOR: '#ffd93d',
                      LOG_INFO_COLOR: '#6bcf7f',
                    }}
                  />
                </div>
              </div>
              
              {/* Expanded stack trace - with proper null guards */}
              {isExpanded && (
                <div className="px-8 pb-2">
                  <div className="text-xs text-ide-textMuted font-mono">
                    <div className="mb-1 text-ide-textDim font-semibold">Stack Trace:</div>
                    
                    {/* Loading state while parsing - safe when details is undefined */}
                    {!details && (
                      <div className="ml-4 text-ide-textDim animate-pulse">Parsing stack trace...</div>
                    )}
                    
                    {/* Parsed frames (clickable) - only render when details exists */}
                    {details && details.frames && details.frames.length > 0 && (
                      details.frames.map((frame, idx) => (
                        <div 
                          key={idx}
                          className="ml-4 hover:bg-ide-surfaceHover px-2 py-0.5 rounded cursor-pointer group flex items-center gap-2"
                          onClick={(e) => { e.stopPropagation(); navigateToError(frame); }}
                          title="Click to jump to this line in the editor"
                        >
                          <Code2 size={10} className="opacity-0 group-hover:opacity-100 text-ide-accent" />
                          <span className="text-ide-accent">{frame.functionName}</span>
                          <span className="text-ide-textDim">@</span>
                          <span className="text-ide-success">
                            {frame.fileName}:{frame.lineNumber}:{frame.columnNumber}
                          </span>
                          <ExternalLink size={10} className="ml-auto opacity-0 group-hover:opacity-100 text-ide-accent" />
                        </div>
                      ))
                    )}
                    
                    {/* Fallback: show raw stack if parsing failed - with null guard */}
                    {details && (!details.frames || details.frames.length === 0) && details.rawStack && (
                      <pre className="ml-4 text-xs text-ide-textMuted whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto bg-ide-elevated p-2 rounded">
                        {details.rawStack}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

